import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, MapPin, ChevronRight, Loader2, Tag, ArrowLeft, Grid3X3, List
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, eventsItemListLD, collectionPageLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  party: 'Party', festival: 'Festival', brunch: 'Brunch', competition: 'Competition',
  tasting: 'Tasting', concert: 'Concert', live_music: 'Live Music', networking: 'Networking',
  wine: 'Wine Events', nightlife: 'Nightlife', club: 'Club', comedy: 'Comedy',
  theater: 'Theatre', performing_arts: 'Performing Arts', dance: 'Dance',
  date_night: 'Date Night', food: 'Food', workshop: 'Workshop', class: 'Class',
  wellness: 'Wellness', fitness: 'Fitness', tour: 'Tour', sports: 'Sports',
  gala: 'Gala', fundraiser: 'Fundraiser', charity: 'Charity', conference: 'Conference',
  family: 'Family · Kids', exhibition: 'Exhibition', popup: 'Pop-up', art: 'Art · Gallery',
  film: 'Film · Screening', literary: 'Literary · Book', halloween: 'Halloween',
  nye: "New Year's Eve", holiday: 'Holiday · Seasonal', other: 'Other',
};

export default function EventTagsPage() {
  const { city = 'denver', tag } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchEvents();
    fetchAllTags();
  }, [city, tag]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hosted-events/tags/${city}/${tag}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTags = async () => {
    try {
      const response = await axios.get(`${API}/hosted-events/tags/${city}`);
      setAllTags(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getEventUrl = (event) => `/events/${event.city}/${event.slug}`;
  const cityName = city.charAt(0).toUpperCase() + city.slice(1);
  const tagName = tag.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // SEO surface — tag pages were previously unindexable noise. Now they get
  // a proper title, description, breadcrumb LD, and per-event JSON-LD so
  // every tagged event becomes individually eligible for Google's Events
  // carousel.
  const seoTitle = `${tagName} Events in ${cityName} — Eventism`;
  const seoDescription =
    events.length > 0
      ? `${events.length} ${tagName.toLowerCase()} event${events.length !== 1 ? 's' : ''} in ${cityName}: ${events
          .slice(0, 3)
          .map((e) => e.name || e.title)
          .join(', ')}. Secure tickets, instant digital wallet delivery.`
      : `Browse upcoming ${tagName.toLowerCase()} events in ${cityName}. Tickets, dates, venues — all in one place from Eventism.`;
  const seoKeywords = [
    `${tagName.toLowerCase()} ${cityName}`,
    `${cityName} ${tagName.toLowerCase()} events`,
    `${tagName.toLowerCase()} events near me`,
    `things to do in ${cityName}`,
    'Eventism events',
  ];

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        structuredData={[
          collectionPageLD(seoTitle, seoDescription),
          breadcrumbLD([
            { name: 'Home', url: '/' },
            { name: 'Events', url: '/events' },
            { name: cityName, url: `/events/${city}` },
            { name: `#${tagName}`, url: `/events/${city}/tags/${tag}` },
          ]),
          eventsItemListLD(events, `${tagName} events in ${cityName}`, `/events/${city}/tags/${tag}`),
        ].filter(Boolean)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" data-testid="tag-breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/events" className="hover:text-foreground">Events</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/events/${city}`} className="hover:text-foreground">{cityName}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">#{tag}</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(`/events/${city}`)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to All Events
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Tag className="h-8 w-8 text-indigo-500" />
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="tag-title">#{tagName}</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            {events.length} event{events.length !== 1 ? 's' : ''} tagged with "{tag}" in {cityName}
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Other Tags (rendered below events on mobile) */}
          <div className="lg:col-span-1 order-last lg:order-first">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag className="h-4 w-4" /> Browse Other Tags
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {allTags.map(t => (
                  <Link key={t.tag} to={`/events/${city}/tags/${t.tag}`}>
                    <Badge 
                      variant={t.tag === tag ? 'default' : 'outline'} 
                      className="cursor-pointer hover:bg-muted"
                    >
                      {t.tag} ({t.count})
                    </Badge>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 order-first lg:order-none">
            {/* View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">
                Showing {events.length} event{events.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
              </div>
            ) : events.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No events found with this tag</p>
                  <Button onClick={() => navigate(`/events/${city}`)}>
                    Browse All Events
                  </Button>
                </CardContent>
              </Card>
            ) : viewMode === 'grid' ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {events.map(event => (
                  <EventCard key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {events.map(event => (
                  <EventListItem key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Event Card Component
function EventCard({ event, onClick }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <Card 
      className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
      data-testid={`tag-event-card-${event.slug}`}
    >
      {event.image_url && (
        <div className="h-48 overflow-hidden relative">
          <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
          {event.is_featured && (
            <Badge className="absolute top-3 right-3 bg-amber-500">Featured</Badge>
          )}
        </div>
      )}
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline">{CATEGORY_LABELS[event.category] || event.category}</Badge>
          {event.age_restriction && (
            <Badge variant="secondary" className="text-xs">{event.age_restriction}</Badge>
          )}
        </div>
        <h3 className="font-semibold text-lg mb-1">{event.name}</h3>
        {event.tagline && (
          <p className="text-sm text-muted-foreground mb-2">{event.tagline}</p>
        )}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {formatDate(event.event_date)}
          </span>
          {event.venue_name && (
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {event.venue_name}
            </span>
          )}
        </div>
        {event.ticket_price_min && (
          <p className="text-indigo-500 font-semibold mt-3">
            From ${event.ticket_price_min}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Event List Item Component
function EventListItem({ event, onClick }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={onClick}>
      <div className="flex">
        {event.image_url && (
          <div className="w-48 h-32 flex-shrink-0">
            <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 p-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[event.category]}</Badge>
            </div>
            <h3 className="font-semibold text-lg">{event.name}</h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(event.event_date)}
              </span>
              {event.venue_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {event.venue_name}
                </span>
              )}
            </div>
          </div>
          {event.ticket_price_min && (
            <div className="text-right">
              <p className="text-indigo-500 font-semibold">From ${event.ticket_price_min}</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
